import { FC } from 'react';
import { Dialog } from 'primereact/dialog';
import { Check } from 'lucide-react';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

export interface FinishedStudentRow {
  id: string;
  name: string;
  currentUnitLabel: string;
  publicUserId: string;
}

interface Props {
  visible: boolean;
  onHide: () => void;
  students: FinishedStudentRow[];
  total: number;
}

const COLORS = {
  text: '#393939',
  secondary: '#686868',
  outline: '#f1f0ee',
  backPage: '#f9f8f5',
  white: '#ffffff',
  checkBg: '#d1fae6',
  checkFg: '#016145',
};

const SANS = "'Atkinson Hyperlegible', sans-serif";
const SORA = "'Sora', sans-serif";
const COL_STUDENT = 260;

const ModalHeader: FC<{ finished: number; total: number }> = ({ finished, total }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
    <span
      style={{
        flexShrink: 0,
        width: 44,
        height: 44,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        background: COLORS.checkBg,
        color: COLORS.checkFg,
      }}
      aria-hidden
    >
      <Check size={24} strokeWidth={2.5} />
    </span>
    <span style={{ fontFamily: SORA, fontWeight: 700, fontSize: 20, lineHeight: '28px', color: COLORS.text }}>
      {finished} of {total} already finished
    </span>
  </div>
);

const UnitCell: FC<{ label: string }> = ({ label }) =>
  label === '—' ? (
    <span style={{ color: COLORS.text }}>—</span>
  ) : (
    <span style={{ color: COLORS.text }}>
      on <span className="tw-font-bold">{label}</span>
    </span>
  );

export const AlreadyFinishedModal: FC<Props> = ({ visible, onHide, students, total }) => {
  const colHeaderStyle = {
    fontFamily: SANS,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '20px',
    color: COLORS.secondary,
    opacity: 0.9,
  } as const;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={<ModalHeader finished={students.length} total={total} />}
      closable
      draggable={false}
      dismissableMask
      appendTo="self"
      style={{ width: 624, maxWidth: '92vw', borderRadius: 32, overflow: 'hidden' }}
      headerStyle={{
        padding: '16px 24px',
        borderBottom: `2px solid ${COLORS.outline}`,
        background: COLORS.white,
      }}
      contentStyle={{ padding: '16px 24px 24px', background: COLORS.white }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p className="tw-text-sm" style={{ margin: 0, fontFamily: SANS, color: COLORS.secondary }}>
          <span className="tw-font-bold">Focus won&rsquo;t apply to these students,</span> since they&rsquo;ve completed
          all target skills in this unit.
        </p>

        <div
          style={{
            border: `2px solid ${COLORS.outline}`,
            borderRadius: 14,
            overflow: 'hidden',
            background: COLORS.white,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              padding: '8px 16px',
              background: COLORS.backPage,
            }}
          >
            <span style={{ ...colHeaderStyle, width: COL_STUDENT }}>Student</span>
            <span style={{ ...colHeaderStyle, flex: '1 0 0' }}>Unit</span>
          </div>

          {students.map(row => (
            <div
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                padding: '12px 16px',
                background: COLORS.white,
                borderBottom: `2px solid ${COLORS.outline}`,
              }}
            >
              <span
                style={{
                  width: COL_STUDENT,
                  fontFamily: SANS,
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: '24px',
                  color: COLORS.text,
                }}
              >
                <MaskedStudentData student={row} type={PiiTypes.FullName}>
                  {row.name}
                </MaskedStudentData>
              </span>
              <span
                style={{
                  flex: '1 0 0',
                  fontFamily: SANS,
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: '24px',
                }}
              >
                <UnitCell label={row.currentUnitLabel} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
};
